/**
 * 音色相关类型定义
 */

/**
 * 音色标签
 */
export interface VoiceLabels {
  accent?: string;
  description?: string;
  age?: string;
  gender?: string;
  useCase?: string;
  language?: string;
}

/**
 * 音色项 API 响应（前端统一 camelCase）
 * 新接口 GET /voices 返回：voiceId、providerVoiceId、name、description、previewUrl、supportedModels
 */
export interface VoiceApiItem {
  voiceId: string;
  providerVoiceId?: string;
  name: string;
  nameCn?: string;
  category?: string;
  language?: string;
  accent?: string;
  accentNameI18n?: Record<string, string>;
  description?: string;
  descriptionCn?: string;
  labels?: VoiceLabels;
  previewUrl?: string;
  supportedModels?: string[];
  verifiedLanguages?: Array<{
    language: string;
    languageName: string;
  }>;
  fineTuning?: {
    isAllowedToFineTune: boolean;
    state: Record<string, unknown>;
    verificationFailures: string[];
    verificationAttemptsCount: number;
    manualVerificationRequested: boolean;
  };
  highQualityBaseModelIds?: string[];
}

/**
 * 音色列表 API 响应
 */
export interface VoicesResponse {
  voices: VoiceApiItem[];
}

/**
 * 组件内部使用的音色类型
 */
export interface Voice {
  id: string;
  providerVoiceId?: string;
  name: string;
  gender: 'male' | 'female' | 'neutral';
  language: string;
  accent?: string;
  accentNameI18n?: Record<string, string>;
  tags: string[];
  previewUrl: string;
  description?: string;
  descriptionCn?: string;
}

/**
 * 将 API 音色转换为组件音色
 * 兼容新接口（无 labels）和旧接口（有 labels）
 */
export function adaptVoice(apiVoice: VoiceApiItem): Voice {
  const gender = apiVoice.labels?.gender?.toLowerCase();
  const tags: string[] = [];

  if (apiVoice.labels?.accent) tags.push(apiVoice.labels.accent);
  if (apiVoice.labels?.age) tags.push(apiVoice.labels.age);
  if (apiVoice.labels?.useCase) tags.push(apiVoice.labels.useCase);
  if (apiVoice.labels?.description) tags.push(apiVoice.labels.description);

  return {
    id: apiVoice.voiceId,
    providerVoiceId: apiVoice.providerVoiceId,
    name: apiVoice.nameCn || apiVoice.name,
    gender: gender === 'male' || gender === 'female' ? gender : 'neutral',
    language: apiVoice.language || apiVoice.labels?.language || 'zh',
    accent: apiVoice.accent || apiVoice.labels?.accent,
    accentNameI18n: apiVoice.accentNameI18n,
    tags: tags.length > 0 ? tags : ['通用'],
    previewUrl: apiVoice.previewUrl || '',
    description: apiVoice.description,
    descriptionCn: apiVoice.descriptionCn,
  };
}

/**
 * 检查音色是否支持中文
 */
export function isChineseVoice(voice: VoiceApiItem): boolean {
  if (voice.language === 'zh' || voice.language?.startsWith('zh-')) {
    return true;
  }

  // 检查 labels.language
  if (voice.labels?.language === 'zh') {
    return true;
  }

  // 检查 verifiedLanguages
  if (voice.verifiedLanguages?.some((lang) => lang.language === 'zh')) {
    return true;
  }

  return false;
}
